/** Mobile image + attachment notice composer (footer: NoticeboardButton only). */
import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  FormHelperText,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { NoticeboardButton } from "../shared";
import { motion } from "framer-motion";
import { useAccessibleCircles } from "../../hooks/useAccessibleCircles";
import { useAuth } from "../../context/AuthContext";
import {
  listMobileChipAudiences,
  noticeAudiencePickPatch,
  selectedMobileChipAudienceId,
  settleNoticeAudience,
  writeNoticeAudiencePatch,
} from "../../domain/notices/noticeAudience";
import MobileNoticeAttachmentSlots from "./MobileNoticeAttachmentSlots";
import PublishScheduleOptions from "../scheduling/PublishScheduleOptions";
import SaveToDriveFolderOptions from "../scheduling/SaveToDriveFolderOptions";

/** Single calm entrance (avoids stacking with Dialog + wizard Container). */
const COMPOSER_ENTER = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.52, ease: [0.22, 1, 0.36, 1] as const },
};

const SECTION_ENTER = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
};

/** Matches `PublishScheduleOptions` section headings. */
const sectionHeadingSx = {
  fontWeight: 400,
  color: "primary.main",
  fontSize: { xs: "1.125rem", sm: "1.25rem" },
};

type UploadedFile = {
  fileUrl: string;
  fileName: string;
  contentType: string;
  size: number;
};

interface MobileAttachmentComposerProps {
  mode?: "attachment" | "image";
  formData: {
    title: string;
    audience?: string;
    targetSubcircle?: string;
    uploadedFiles?: UploadedFile[];
    publishMode?: "now" | "schedule";
    publishAt?: string;
    driveFolderKey?: string;
  };
  errors: Record<string, string | undefined>;
  isUploading?: boolean;
  uploadStatusMessage?: string;
  uploadProgress?: import("../../utils/presignedUpload").NoticeUploadProgressState | null;
  onCancelUpload?: () => void;
  isSubmitting?: boolean;
  handleChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | { target: { name: string; value: any } },
  ) => void;
  applyFormPatch?: (patch: Record<string, unknown>) => void;
  handleFileUpload?: (files: FileList) => Promise<void>;
  handleFileRemove?: (index: number) => void;
  handleSubmit: (
    e: React.FormEvent,
    skipQualityCheck?: boolean,
    forceSubmit?: boolean,
  ) => Promise<void>;
}

const MobileAttachmentComposer: React.FC<MobileAttachmentComposerProps> = ({
  mode = "attachment",
  formData,
  errors,
  isUploading,
  uploadStatusMessage,
  uploadProgress,
  onCancelUpload,
  isSubmitting,
  handleChange,
  applyFormPatch,
  handleFileUpload,
  handleFileRemove,
  handleSubmit,
}) => {
  const { user } = useAuth();
  const { subcircles, canAccessMainCircle } = useAccessibleCircles();
  const audienceCatalog = useMemo(
    () => ({
      canChooseEveryone: canAccessMainCircle,
      circles: subcircles,
    }),
    [canAccessMainCircle, subcircles],
  );
  const [showAudienceRequiredError, setShowAudienceRequiredError] =
    useState(false);
  const [publishAtError, setPublishAtError] = useState<string | null>(null);
  const uploadedFiles = useMemo(
    () => formData.uploadedFiles ?? [],
    [formData.uploadedFiles],
  );
  const isImageMode = mode === "image";

  React.useEffect(() => {
    const settled = settleNoticeAudience("mobileChips", audienceCatalog, {
      audience: formData.audience,
      targetSubcircle: formData.targetSubcircle,
    });
    if (settled) {
      writeNoticeAudiencePatch(settled, applyFormPatch, handleChange);
    }
  }, [
    applyFormPatch,
    audienceCatalog,
    formData.audience,
    formData.targetSubcircle,
    handleChange,
  ]);

  const selectAudience = (value: string) => {
    setShowAudienceRequiredError(false);
    writeNoticeAudiencePatch(
      noticeAudiencePickPatch(value),
      applyFormPatch,
      handleChange,
    );
  };

  const selectedAudienceValue = selectedMobileChipAudienceId(
    {
      audience: formData.audience,
      targetSubcircle: formData.targetSubcircle,
    },
    canAccessMainCircle,
  );
  const mobileChipAudiences = listMobileChipAudiences(audienceCatalog);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        if (!formData.audience) {
          e.preventDefault();
          setShowAudienceRequiredError(true);
          return;
        }
        if (formData.publishMode === "schedule") {
          const publishAt = formData.publishAt || "";
          if (!publishAt.trim()) {
            e.preventDefault();
            setPublishAtError("Choose a publish date and time");
            return;
          }
          const publishMs = new Date(publishAt).getTime();
          if (Number.isNaN(publishMs) || publishMs <= Date.now() + 60_000) {
            e.preventDefault();
            setPublishAtError(
              "Scheduled time must be at least 1 minute in the future"
            );
            return;
          }
        }
        setPublishAtError(null);
        handleSubmit(e, false, true);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        component={motion.div}
        initial={COMPOSER_ENTER.initial}
        animate={COMPOSER_ENTER.animate}
        transition={COMPOSER_ENTER.transition}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pr: { xs: 9, sm: 2 },
            flexShrink: 0,
            /* Safe area + row for CreationWizard close control (notch / status bar) */
            pt: {
              xs: "calc(env(safe-area-inset-top, 0px) + 52px)",
              sm: 2,
            },
            pb: 1,
          }}
        >
          <Typography
            variant="h5"
            sx={{ color: "primary.main", fontWeight: 500 }}
          >
            {isImageMode ? "Image notice" : "Create Attachment Notice"}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2.5, pt: 2.25, pb: 2.5, overflowY: "auto", flex: 1 }}>
          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.05 }}
            sx={{ mb: 2, mt: 0.5, display: "flex", justifyContent: "center" }}
          >
            <Box
              component="img"
              src="/icons/notice-create.webp"
              alt="Notice create icon"
              loading="lazy"
              decoding="async"
              sx={{
                width: "100%",
                maxWidth: 220,
                height: "auto",
                maxHeight: 140,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.11 }}
          >
            <TextField
              fullWidth
              size="small"
              name="title"
              autoComplete="off"
              placeholder="What is this notice about?"
              value={formData.title || ""}
              onChange={(e) => {
                e.stopPropagation();
                handleChange({
                  target: { name: "title", value: e.target.value },
                });
              }}
              error={!!errors.title}
              helperText={
                errors.title && !formData.title?.trim() ? errors.title : undefined
              }
              inputProps={{ maxLength: 100 }}
              sx={{ mb: 2.5 }}
            />
          </Box>

          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.14 }}
            sx={{ mb: 2.5 }}
          >
            <PublishScheduleOptions
              publishMode={formData.publishMode || "now"}
              publishAt={formData.publishAt || ""}
              onPublishModeChange={(mode) => {
                setPublishAtError(null);
                handleChange({ target: { name: "publishMode", value: mode } });
              }}
              onPublishAtChange={(value) => {
                setPublishAtError(null);
                handleChange({ target: { name: "publishAt", value } });
              }}
            />
            {(publishAtError || errors.publishAt) && (
              <FormHelperText error sx={{ mt: 0.5 }}>
                {publishAtError || errors.publishAt}
              </FormHelperText>
            )}
          </Box>

          <Box
            component={motion.div}
            initial={SECTION_ENTER.initial}
            animate={SECTION_ENTER.animate}
            transition={{ duration: 0.34, ease: "easeOut", delay: 0.17 }}
            sx={{
              mb: 3,
            }}
          >
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 1 }}>
              Choose who should receive this notice
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 1,
                overflowX: "auto",
                overflowY: "hidden",
                pb: 0.25,
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {mobileChipAudiences.map((chip) => {
                const isSelected = selectedAudienceValue === chip.id;
                return (
                  <Button
                    key={chip.id}
                    type="button"
                    onClick={() => selectAudience(chip.id)}
                    sx={{
                      flexShrink: 0,
                      px: 1.5,
                      py: 0.45,
                      minWidth: "fit-content",
                      borderRadius: 999,
                      border: "1px solid",
                      borderColor: isSelected ? "primary.main" : "divider",
                      backgroundColor: isSelected
                        ? "primary.50"
                        : "background.paper",
                      color: isSelected ? "primary.main" : "text.secondary",
                      textTransform: "none",
                      fontWeight: 500,
                      fontSize: "0.82rem",
                    }}
                  >
                    {chip.label}
                  </Button>
                );
              })}
            </Box>

            {(showAudienceRequiredError ||
              errors.audience ||
              errors.targetSubcircle) && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.75 }}
              >
                {errors.audience ||
                  errors.targetSubcircle ||
                  "Please select who should receive this notice"}
              </Typography>
            )}

            <SaveToDriveFolderOptions
              audience={formData.audience || ""}
              targetSubcircle={formData.targetSubcircle}
              audienceLabel={
                formData.audience === "circle"
                  ? user?.schoolName || "Entire Circle"
                  : subcircles.find(
                      (s) => s.circleId === formData.targetSubcircle,
                    )?.name || ""
              }
              driveFolderKey={formData.driveFolderKey || ""}
              publishMode={formData.publishMode || "now"}
              onDriveFolderKeyChange={(value) =>
                handleChange({ target: { name: "driveFolderKey", value } })
              }
            />
          </Box>

          <MobileNoticeAttachmentSlots
            embedded={false}
            mode={mode}
            uploadedFiles={uploadedFiles}
            errors={errors}
            isUploading={isUploading}
            uploadStatusMessage={uploadStatusMessage}
            uploadProgress={uploadProgress}
            onCancelUpload={onCancelUpload}
            handleFileUpload={handleFileUpload}
            handleFileRemove={handleFileRemove}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            px: 2,
            py: 1.5,
            pb: "calc(env(safe-area-inset-bottom, 0px) + 16px)",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <NoticeboardButton
            type="submit"
            variant="primary"
            disabled={!!isUploading || !!isSubmitting}
            startIcon={
              formData.publishMode === "schedule" && !isSubmitting ? (
                <ScheduleIcon sx={{ fontSize: 20 }} />
              ) : undefined
            }
            sx={{
              width: "100%",
              py: 1.25,
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {isSubmitting
              ? formData.publishMode === "schedule"
                ? "Scheduling…"
                : "Posting…"
              : formData.publishMode === "schedule"
                ? "Schedule Notice"
                : "Post Notice"}
          </NoticeboardButton>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            <LockOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              Review your notice details before posting
            </Typography>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileAttachmentComposer;
